import { setup, fromPromise } from 'xstate';
import { ActorErrorParams } from '../../../../lib/types';
import {
  FraudAlloyMachineEvents,
  SendStartIDUploadJourneyParams,
} from './types/events';
import {
  InitializingFraudAlloyResult,
  PollIDUploadJourneyStatusKYCResult,
  PollIDUploadJourneyStatusResult,
  StartIDUploadJourneyResult,
} from './types/actors';
import {
  getInitializeFraudAlloyParams,
  getServiceErrorParams,
  emitIDUploadJourneyFailure,
  emitIDUploadJourneySuccess,
  emitFraudAlloyMachineReady,
  getPollIDUploadJourneyStatusParams,
  getPollIDUploadJourneyStatusKYCParams,
  emitIDUploadJourneyPending,
} from './actions';
import { assignFraudAlloyInitResponse } from './assigns';
import {
  getPollIDUploadJourneyStatusInput,
  getStartIDUploadJourneyInput,
} from './inputs';

export interface FraudAlloyContext {
  neuroIdSiteId: string | null;
  neuroUserId: string | null;
  iovationBlackboxId: string | null;
}

export const fraudAlloyMachine = setup({
  types: {
    context: {} as FraudAlloyContext,
    events: {} as FraudAlloyMachineEvents,
  },
  guards: {
    isKYCAlloy: (_, __: SendStartIDUploadJourneyParams) => {
      throw new Error('Not implemented');
    },
    isFraudJourneyStatusSuccess: (
      _,
      __: PollIDUploadJourneyStatusResult
    ): boolean => {
      throw new Error('Not implemented');
    },
    isFraudJourneyStatusPending: (
      _,
      __: PollIDUploadJourneyStatusResult
    ): boolean => {
      throw new Error('Not implemented');
    },
    isFraudJourneyStatusKYCSuccess: (
      _,
      __: PollIDUploadJourneyStatusKYCResult
    ): boolean => {
      throw new Error('Not implemented');
    },
    isFraudJourneyStatusKYCPending: (
      _,
      __: PollIDUploadJourneyStatusKYCResult
    ): boolean => {
      throw new Error('Not implemented');
    },
  },
  actions: {
    assignFraudAlloyInitResponse,
    emitFraudAlloyMachineReady,
    emitIDUploadJourneySuccess,
    emitIDUploadJourneyFailure,
    emitIDUploadJourneyPending,
    logStartIDUploadJourneyFailure: (
      _,
      __: StartIDUploadJourneyResult
    ): void => {
      throw new Error('Not implemented');
    },
    logPollJourneyStatusFailure: (
      _,
      __: PollIDUploadJourneyStatusResult | PollIDUploadJourneyStatusKYCResult
    ): void => {
      throw new Error('Not implemented');
    },
    logError: (_, __: ActorErrorParams): void => {
      throw new Error('Not implemented');
    },
  },
  actors: {
    initializeFraudAlloy: fromPromise<InitializingFraudAlloyResult>(
      async () => {
        throw new Error('Not implemented');
      }
    ),
    startIDUploadJourney: fromPromise<
      StartIDUploadJourneyResult,
      SendStartIDUploadJourneyParams
    >(async () => {
      throw new Error('Not implemented');
    }),
    startIDUploadJourneyKYC: fromPromise<
      StartIDUploadJourneyResult,
      SendStartIDUploadJourneyParams
    >(async () => {
      throw new Error('Not implemented');
    }),
    pollIDUploadJourneyStatus: fromPromise<
      PollIDUploadJourneyStatusResult,
      StartIDUploadJourneyResult
    >(async () => {
      throw new Error('Not implemented');
    }),
    pollIDUploadJourneyStatusKYC: fromPromise<
      PollIDUploadJourneyStatusKYCResult,
      StartIDUploadJourneyResult
    >(async () => {
      throw new Error('Not implemented');
    }),
  },
}).createMachine({
  id: 'fraudAlloyMachine',
  context: {
    neuroIdSiteId: null,
    neuroUserId: null,
    iovationBlackboxId: null,
  },
  initial: 'initializingFraudAlloy',
  on: {
    SEND_INITIALIZE_FRAUD_ALLOY: {
      target: '.initializingFraudAlloy',
    },
  },
  states: {
    initializingFraudAlloy: {
      invoke: {
        src: 'initializeFraudAlloy',
        onDone: [
          {
            actions: [
              {
                type: 'assignFraudAlloyInitResponse',
                params: getInitializeFraudAlloyParams,
              },
            ],
            target: 'ready',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            // Always go to ready
            target: 'ready',
          },
        ],
      },
    },
    initializingFraudAlloyKYC: {},
    ready: {
      entry: ['emitFraudAlloyMachineReady'],
      on: {
        SEND_START_ID_UPLOAD_JOURNEY: [
          {
            guard: {
              type: 'isKYCAlloy',
              params: getStartIDUploadJourneyInput,
            },
            target: '.startingIDUploadJourneyKYC',
          },
          {
            target: '.startingIDUploadJourney',
          },
        ],
      },
      initial: 'initial',
      states: {
        initial: {},
        startingIDUploadJourney: {
          invoke: {
            src: 'startIDUploadJourney',
            input: getStartIDUploadJourneyInput,
            onDone: [
              {
                target: 'pollingIDUploadJourneyStatus',
              },
            ],
            onError: [
              {
                actions: [
                  {
                    type: 'logError',
                    params: getServiceErrorParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
                target: 'failure',
              },
            ],
          },
        },
        startingIDUploadJourneyKYC: {
          invoke: {
            src: 'startIDUploadJourneyKYC',
            input: getStartIDUploadJourneyInput,
            onDone: [
              {
                target: 'pollingIDUploadJourneyStatusKYC',
              },
            ],
            onError: [
              {
                actions: [
                  {
                    type: 'logError',
                    params: getServiceErrorParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
                target: 'failure',
              },
            ],
          },
        },
        pollingIDUploadJourneyStatus: {
          invoke: {
            src: 'pollIDUploadJourneyStatus',
            input: getPollIDUploadJourneyStatusInput,
            onDone: [
              {
                guard: {
                  type: 'isFraudJourneyStatusSuccess',
                  params: getPollIDUploadJourneyStatusParams,
                },
                actions: [
                  {
                    type: 'emitIDUploadJourneySuccess',
                  },
                ],
                target: 'success',
              },
              {
                guard: {
                  type: 'isFraudJourneyStatusPending',
                  params: getPollIDUploadJourneyStatusParams,
                },
                actions: [
                  {
                    type: 'emitIDUploadJourneyPending',
                  },
                ],
                target: 'pending',
              },
              {
                target: 'failure',
                actions: [
                  {
                    type: 'logPollJourneyStatusFailure',
                    params: getPollIDUploadJourneyStatusParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
              },
            ],
            onError: [
              {
                actions: [
                  {
                    type: 'logError',
                    params: getServiceErrorParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
                target: 'failure',
              },
            ],
          },
        },
        pollingIDUploadJourneyStatusKYC: {
          invoke: {
            src: 'pollIDUploadJourneyStatusKYC',
            input: getPollIDUploadJourneyStatusInput,
            onDone: [
              {
                guard: {
                  type: 'isFraudJourneyStatusKYCSuccess',
                  params: getPollIDUploadJourneyStatusKYCParams,
                },
                actions: [
                  {
                    type: 'emitIDUploadJourneySuccess',
                  },
                ],
                target: 'success',
              },
              {
                guard: {
                  type: 'isFraudJourneyStatusKYCPending',
                  params: getPollIDUploadJourneyStatusKYCParams,
                },
                actions: [
                  {
                    type: 'emitIDUploadJourneyPending',
                  },
                ],
                target: 'pending',
              },
              {
                target: 'failure',
                actions: [
                  {
                    type: 'logPollJourneyStatusFailure',
                    params: getPollIDUploadJourneyStatusKYCParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
              },
            ],
            onError: [
              {
                actions: [
                  {
                    type: 'logError',
                    params: getServiceErrorParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
                target: 'failure',
              },
            ],
          },
        },
        success: {},
        failure: {},
        pending: {},
      },
    },
  },
});
